import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * How long to keep waiting for a hash target to appear, in animation frames.
 * Page components are lazy chunks, so on a cross-page hash link the element
 * does not exist when the route changes -- it arrives once the chunk lands.
 * ~2s at 60fps, then we give up and go to the top rather than leaving the
 * viewport wherever the previous page left it.
 */
const MAX_FRAMES = 120;

/** Breathing room between the sticky header and the heading it would cover. */
const HEADER_GAP = 12;

/**
 * Scroll behaviour for route changes.
 *
 * Without this the app called `window.scrollTo(0, 0)` on every pathname change
 * and never looked at the hash, so a link like `/#assessment` or
 * `/services/ai-driven-validation#framework-heading` landed at the top of the
 * page. Pressing Enter in the address bar afterwards worked, because by then
 * the target was mounted and the browser did its own anchor jump -- which is
 * exactly the race this hook closes.
 *
 * The offset matters too: the header is `sticky top-0`, so scrolling an element
 * to y=0 hides it underneath.
 */
export function useRouteScroll(): void {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    let cancelled = false;
    let frames = 0;

    const attempt = () => {
      if (cancelled) return;

      const target = document.getElementById(id);
      if (target) {
        const header = document.querySelector('header');
        const offset = (header?.getBoundingClientRect().height ?? 0) + HEADER_GAP;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
        return;
      }

      if (frames++ < MAX_FRAMES) {
        requestAnimationFrame(attempt);
      } else {
        window.scrollTo(0, 0);
      }
    };

    // A transition can move an already-mounted hash target after our first
    // measurement. Align it again when the new page finishes entering.
    window.addEventListener('waq:page-ready', attempt);
    requestAnimationFrame(attempt);

    return () => {
      cancelled = true;
      window.removeEventListener('waq:page-ready', attempt);
    };
    // `key` is in the deps so clicking the same hash link twice scrolls again.
  }, [pathname, hash, key]);
}
